import type { UIEvent } from "react";

/**
 * Digitación es UNA sola vista: ni la página ni el mosaico se desplazan
 * nunca. Lo que no cabe --las líneas de un documento largo, la cola-- se
 * desplaza por dentro, en su región, con su propia barra.
 *
 * Tres cosas lo rompían, y por eso esto existe:
 * - `focus()` desplaza a TODOS los ancestros hasta que el elemento se ve,
 *   también los `overflow: hidden`: un Tab a la última línea subía la
 *   pantalla entera.
 * - `scrollIntoView()`, lo mismo.
 * - Una fila de grilla sin `minmax(0, 1fr)` crece con su contenido: la
 *   columna con `overflow-y-auto` nunca se desplazaba y empujaba al resto.
 * - Lo absoluto sin un ancestro posicionado (las etiquetas `sr-only` de las
 *   líneas) escapa de todos los `overflow: hidden` y estira el DOCUMENTO: la
 *   rueda desplazaba la página. Toda región que se desplaza es `relative`.
 * Lo del CSS va en cada grilla y región; lo demás, aquí.
 */

/** Lo que sí se desplaza por dentro lleva este atributo. */
export const SCROLL_REGION = "data-scroll-region";

/** Lo que nunca se desplaza: la raíz, el área de trabajo y el mosaico. */
export const NO_SCROLL = "data-no-scroll";

/**
 * Pone a la vista `element` dentro de SU región --la más cercana que se
 * desplaza por dentro-- y en ninguna otra parte.
 */
export const reveal = (element: HTMLElement): void => {
  const region = element.parentElement?.closest<HTMLElement>(
    `[${SCROLL_REGION}]`,
  );
  if (region == null) return;
  const box = element.getBoundingClientRect();
  const frame = region.getBoundingClientRect();
  if (box.top < frame.top) region.scrollTop -= frame.top - box.top;
  else if (box.bottom > frame.bottom) {
    region.scrollTop += Math.min(
      box.bottom - frame.bottom,
      box.top - frame.top,
    );
  }
};

/** Llevar el foco sin desplazar nada más que su región. */
export const focusInPlace = (element: HTMLElement | null | undefined): void => {
  if (element == null) return;
  element.focus({ preventScroll: true });
  reveal(element);
};

/**
 * Para el `onScroll` de lo que nunca se desplaza: si algo --el navegador al
 * enfocar con el ratón, buscar en la página-- lo movió, vuelve a su sitio.
 */
export const pinScroll = (event: UIEvent<HTMLElement>): void => {
  const target = event.currentTarget;
  if (target.scrollTop !== 0) target.scrollTop = 0;
  if (target.scrollLeft !== 0) target.scrollLeft = 0;
};
